import { useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { ArrowDownSLineIcon, ArrowUpSLineIcon } from 'components/icons'
import { FilterPopoverProps } from './types'

export default function FilterPopoverMui( {
	label,
	children,
	hasSearch,
	searchVal,
	setSearchVal,
	isLoading,
	renderFooter,
	numFilters,
	numActiveFilters,
	hasActiveFilters,
}: FilterPopoverProps ) {
	const [ anchorEl, setAnchorEl ] = useState<HTMLElement | null>( null )
	const isPopoverOpen = anchorEl !== null
	const closePopover = () => setAnchorEl( null )

	// Mirrors EuiFilterButton: the badge counts active filters once any are on, otherwise available ones.
	const badgeCount = hasActiveFilters ? numActiveFilters : numFilters

	return (
		<>
			<Button
				variant="outlined"
				color="inherit"
				aria-expanded={isPopoverOpen}
				endIcon={isPopoverOpen ? <ArrowUpSLineIcon /> : <ArrowDownSLineIcon />}
				onClick={( e ) => {
					const target = e.currentTarget
					setAnchorEl( ( prev ) => ( prev ? null : target ) )
				}}
			>
				{label}
				{ badgeCount !== undefined && (
					<Chip
						component="span"
						label={badgeCount}
						color={hasActiveFilters ? 'primary' : 'default'}
						aria-label={hasActiveFilters ? `${badgeCount} active filters` : `${badgeCount} available filters`}
						sx={{ ml: 1 }}
					/>
				) }
			</Button>
			<Popover
				open={isPopoverOpen}
				anchorEl={anchorEl}
				onClose={closePopover}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
			>
				{ hasSearch && setSearchVal && (
					<>
						<Box p={1}>
							<TextField
								type="search"
								fullWidth
								value={searchVal}
								onChange={( e ) => setSearchVal( e as React.ChangeEvent<HTMLInputElement> )}
							/>
						</Box>
						<Divider />
					</>
				) }
				<Box>
					{children}
				</Box>
				{ isLoading && (
					<Stack alignItems="center" gap={0.5} p={2}>
						<CircularProgress size={24} />
						<Typography variant="body2">Loading...</Typography>
					</Stack>
				) }
				{ renderFooter?.( { closePopover } ) }
			</Popover>
		</>
	)
}
